import { createFileRoute } from '@tanstack/react-router';
import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Search, Save, RotateCcw } from 'lucide-react';
import { apiRequest, ApiError } from '../lib/api';
import { useToast } from '../lib/toast';
import { ListShell } from '../components/ListShell';
import { Button } from '../components/ui/Button';
import { EmptyState } from '../components/ui/EmptyState';
import { ErrorState } from '../components/ui/ErrorState';
import { SelectField } from '../components/ui/Field';
import { useDebouncedValue } from '../components/people/useDebouncedValue';
import type { StudentRecord } from '../components/people/types';
import {
  PROGRESS_STATUS_OPTIONS,
  type CourseSummary,
  type CourseWithLessons,
  type LessonSummary,
  type ProgressEntryRow,
  type ProgressStatus,
} from '../components/academics/types';

interface MergedRow {
  lessonId: string;
  lessonTitle: string;
  orderIndex: number;
  courseId: string;
  courseName: string;
  status: ProgressStatus;
  notes: string;
  baseline: { status: ProgressStatus; notes: string };
}

export const Route = createFileRoute('/_authed/academics/progress')({
  component: ProgressPage,
});

function ProgressPage() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const [search, setSearch] = useState('');
  const debounced = useDebouncedValue(search.trim(), 300);
  const [studentId, setStudentId] = useState<string | null>(null);
  const [courseFocusId, setCourseFocusId] = useState<string>('');
  const [rows, setRows] = useState<MergedRow[]>([]);

  const studentsQuery = useQuery({
    queryKey: ['students', 'progress-search', debounced] as const,
    queryFn: ({ signal }) =>
      apiRequest<{ items: StudentRecord[]; total: number }>('/api/students', {
        signal,
        query: {
          page: 1,
          pageSize: 30,
          status: 'active',
          ...(debounced ? { q: debounced } : {}),
        },
      }),
    placeholderData: (prev) => prev,
  });

  const coursesQuery = useQuery({
    queryKey: ['courses', 'progress'] as const,
    queryFn: ({ signal }) =>
      apiRequest<{ items: CourseSummary[] }>('/api/courses', { signal }),
    staleTime: 60_000,
  });

  const progressQuery = useQuery({
    queryKey: ['progress', studentId] as const,
    enabled: Boolean(studentId),
    queryFn: ({ signal }) =>
      apiRequest<{ items: ProgressEntryRow[] }>(
        `/api/progress/by-student/${studentId ?? ''}`,
        { signal },
      ),
  });

  const focusedCourseQuery = useQuery({
    queryKey: ['courses', 'detail', courseFocusId] as const,
    enabled: Boolean(courseFocusId),
    queryFn: ({ signal }) =>
      apiRequest<CourseWithLessons>(`/api/courses/${courseFocusId}`, { signal }),
  });

  // Merge: existing progress rows + any focused-course lessons that don't yet
  // have a record (rendered as not_started so admins can prime new lessons).
  useEffect(() => {
    if (!studentId) {
      setRows([]);
      return;
    }
    const existing = progressQuery.data?.items ?? [];
    const focusedLessons: LessonSummary[] =
      focusedCourseQuery.data?.lessons ?? [];
    const focusedCourse = focusedCourseQuery.data;

    const merged: MergedRow[] = [];
    const lessonIds = new Set<string>();

    for (const row of existing) {
      lessonIds.add(row.lessonId);
      merged.push({
        lessonId: row.lessonId,
        lessonTitle: row.lessonTitle,
        orderIndex: row.orderIndex,
        courseId: row.courseId,
        courseName: row.courseName,
        status: row.status,
        notes: row.notes ?? '',
        baseline: { status: row.status, notes: row.notes ?? '' },
      });
    }

    if (focusedCourse) {
      for (const lesson of focusedLessons) {
        if (lessonIds.has(lesson.id)) continue;
        merged.push({
          lessonId: lesson.id,
          lessonTitle: lesson.title,
          orderIndex: lesson.orderIndex,
          courseId: focusedCourse.id,
          courseName: focusedCourse.name,
          status: 'not_started',
          notes: '',
          baseline: { status: 'not_started', notes: '' },
        });
      }
    }

    merged.sort((a, b) => {
      if (a.courseName === b.courseName) return a.orderIndex - b.orderIndex;
      return a.courseName.localeCompare(b.courseName);
    });
    setRows(merged);
  }, [studentId, progressQuery.data, focusedCourseQuery.data]);

  const dirtyRows = useMemo(
    () =>
      rows.filter(
        (r) => r.status !== r.baseline.status || r.notes !== r.baseline.notes,
      ),
    [rows],
  );

  const saveMutation = useMutation({
    mutationFn: () => {
      if (!studentId) throw new Error('No student selected');
      if (dirtyRows.length === 0) {
        return Promise.resolve({ ok: true as const, count: 0 });
      }
      return apiRequest<{ ok: true; count: number }>(
        `/api/progress/by-student/${studentId}`,
        {
          method: 'POST',
          body: {
            entries: dirtyRows.map((r) => ({
              lessonId: r.lessonId,
              status: r.status,
              ...(r.notes ? { notes: r.notes } : {}),
            })),
          },
        },
      );
    },
    onSuccess: () => {
      if (studentId) {
        void queryClient.invalidateQueries({ queryKey: ['progress', studentId] });
      }
      toast('Progress saved.', { variant: 'success' });
    },
    onError: (err: unknown) => {
      toast(err instanceof ApiError ? err.message : 'Could not save progress.', {
        variant: 'error',
      });
    },
  });

  const grouped = useMemo(() => {
    const map = new Map<string, MergedRow[]>();
    for (const r of rows) {
      const arr = map.get(r.courseName) ?? [];
      arr.push(r);
      map.set(r.courseName, arr);
    }
    return Array.from(map.entries());
  }, [rows]);

  const selectedStudent = useMemo(
    () => (studentsQuery.data?.items ?? []).find((s) => s.id === studentId),
    [studentsQuery.data, studentId],
  );

  const courseOptions = useMemo(
    () => [
      { value: '', label: 'Choose a course to merge lessons' },
      ...(coursesQuery.data?.items ?? []).map((c) => ({
        value: c.id,
        label: c.name,
      })),
    ],
    [coursesQuery.data],
  );

  return (
    <ListShell
      title="Student progress"
      description="Track lesson-level progress for each student."
      primaryAction={
        <div className="flex items-center gap-2">
          <Button
            variant="secondary"
            icon={<RotateCcw size={14} aria-hidden="true" />}
            onClick={() => {
              setRows((prev) =>
                prev.map((r) => ({
                  ...r,
                  status: r.baseline.status,
                  notes: r.baseline.notes,
                })),
              );
            }}
            disabled={dirtyRows.length === 0}
          >
            Discard
          </Button>
          <Button
            icon={<Save size={14} aria-hidden="true" />}
            loading={saveMutation.isPending}
            disabled={dirtyRows.length === 0}
            onClick={() => saveMutation.mutate()}
          >
            Save changes {dirtyRows.length > 0 ? `(${dirtyRows.length})` : ''}
          </Button>
        </div>
      }
    >
      <div className="grid gap-4 md:grid-cols-[280px_1fr]">
        <aside className="rounded-lg border border-[color:var(--color-border)] bg-[color:var(--color-bg-elevated)] p-3 h-fit">
          <label
            htmlFor="progress-search"
            className="text-xs font-semibold uppercase tracking-wide text-[color:var(--color-text-muted)]"
          >
            Find a student
          </label>
          <div className="relative mt-2">
            <span
              aria-hidden="true"
              className="absolute left-3 top-1/2 -translate-y-1/2 text-[color:var(--color-text-muted)]"
            >
              <Search size={14} />
            </span>
            <input
              id="progress-search"
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Name or admission #"
              className="w-full rounded-md border border-[color:var(--color-border)] bg-[color:var(--color-bg)] pl-9 pr-3 py-2 text-sm focus:border-gold-500 focus:outline-none focus:ring-1 focus:ring-gold-500"
            />
          </div>
          <ul className="mt-3 max-h-[55vh] overflow-y-auto divide-y divide-[color:var(--color-border)] rounded-md border border-[color:var(--color-border)]">
            {(studentsQuery.data?.items ?? []).length === 0 ? (
              <li className="px-3 py-4 text-center text-xs text-[color:var(--color-text-muted)]">
                {studentsQuery.isLoading ? 'Loading…' : 'No students.'}
              </li>
            ) : (
              (studentsQuery.data?.items ?? []).map((s) => {
                const isActive = s.id === studentId;
                return (
                  <li key={s.id}>
                    <button
                      type="button"
                      onClick={() => setStudentId(s.id)}
                      aria-current={isActive ? 'true' : undefined}
                      className={
                        isActive
                          ? 'block w-full px-3 py-2 text-left text-sm bg-gold-50 text-gold-700'
                          : 'block w-full px-3 py-2 text-left text-sm hover:bg-[color:var(--color-surface)]'
                      }
                    >
                      <p className="font-medium">{s.name}</p>
                      <p className="text-xs text-[color:var(--color-text-muted)] font-mono">
                        {s.admissionNumber}
                      </p>
                    </button>
                  </li>
                );
              })
            )}
          </ul>
        </aside>

        <section>
          {!studentId ? (
            <EmptyState
              title="Pick a student"
              description="Use the search to choose a student and view their progress."
            />
          ) : progressQuery.isError ? (
            <ErrorState
              title="Could not load progress"
              description={
                progressQuery.error instanceof ApiError
                  ? progressQuery.error.message
                  : undefined
              }
              onRetry={() => progressQuery.refetch()}
            />
          ) : (
            <div className="space-y-4">
              <div className="rounded-lg border border-[color:var(--color-border)] bg-[color:var(--color-bg-elevated)] p-4">
                <p className="text-xs uppercase tracking-wide text-[color:var(--color-text-muted)] font-semibold">
                  Selected student
                </p>
                <p className="mt-1 font-semibold">
                  {selectedStudent ? selectedStudent.name : studentId}
                </p>
                {selectedStudent ? (
                  <p className="text-xs text-[color:var(--color-text-muted)] font-mono">
                    {selectedStudent.admissionNumber}
                  </p>
                ) : null}
                <div className="mt-4">
                  <SelectField
                    label="Add lessons from course"
                    hint="Includes lessons even if no progress has been recorded yet."
                    options={courseOptions}
                    value={courseFocusId}
                    onChange={(e) => setCourseFocusId(e.target.value)}
                  />
                </div>
              </div>

              {progressQuery.isLoading ? (
                <p className="text-sm text-[color:var(--color-text-muted)]">Loading progress…</p>
              ) : grouped.length === 0 ? (
                <EmptyState
                  title="No progress recorded"
                  description="Pick a course above to add lessons and start tracking."
                />
              ) : (
                grouped.map(([courseName, items]) => (
                  <div
                    key={courseName}
                    className="overflow-hidden rounded-lg border border-[color:var(--color-border)] bg-[color:var(--color-bg-elevated)]"
                  >
                    <header className="border-b border-[color:var(--color-border)] bg-[color:var(--color-surface)] px-4 py-2">
                      <h3 className="font-semibold text-sm">{courseName}</h3>
                    </header>
                    <table className="w-full text-sm">
                      <thead>
                        <tr>
                          <th className="px-4 py-2 text-left text-xs font-semibold uppercase tracking-wide text-[color:var(--color-text-muted)]">
                            Lesson
                          </th>
                          <th className="px-4 py-2 text-left text-xs font-semibold uppercase tracking-wide text-[color:var(--color-text-muted)]">
                            Status
                          </th>
                          <th className="px-4 py-2 text-left text-xs font-semibold uppercase tracking-wide text-[color:var(--color-text-muted)]">
                            Notes
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {items.map((row) => {
                          const isDirty =
                            row.status !== row.baseline.status ||
                            row.notes !== row.baseline.notes;
                          return (
                            <tr
                              key={row.lessonId}
                              className="border-t border-[color:var(--color-border)]"
                            >
                              <td className="px-4 py-2 align-middle">
                                <div className="flex items-center gap-2">
                                  <span className="font-mono text-xs text-[color:var(--color-text-muted)]">
                                    {row.orderIndex.toString().padStart(2, '0')}
                                  </span>
                                  <span className="font-medium text-[color:var(--color-text)]">
                                    {row.lessonTitle}
                                  </span>
                                  {isDirty ? (
                                    <span
                                      className="inline-block h-1.5 w-1.5 rounded-full bg-gold-500"
                                      aria-label="Unsaved changes"
                                    />
                                  ) : null}
                                </div>
                              </td>
                              <td className="px-4 py-2 align-middle">
                                <select
                                  aria-label={`Status for ${row.lessonTitle}`}
                                  value={row.status}
                                  onChange={(e) =>
                                    setRows((prev) =>
                                      prev.map((r) =>
                                        r.lessonId === row.lessonId
                                          ? {
                                              ...r,
                                              status: e.target.value as ProgressStatus,
                                            }
                                          : r,
                                      ),
                                    )
                                  }
                                  className="rounded-md border border-[color:var(--color-border)] bg-[color:var(--color-bg)] px-2 py-1 text-sm focus:border-gold-500 focus:outline-none focus:ring-1 focus:ring-gold-500"
                                >
                                  {PROGRESS_STATUS_OPTIONS.map((opt) => (
                                    <option key={opt.value} value={opt.value}>
                                      {opt.label}
                                    </option>
                                  ))}
                                </select>
                              </td>
                              <td className="px-4 py-2 align-middle">
                                <input
                                  type="text"
                                  aria-label={`Notes for ${row.lessonTitle}`}
                                  value={row.notes}
                                  onChange={(e) =>
                                    setRows((prev) =>
                                      prev.map((r) =>
                                        r.lessonId === row.lessonId
                                          ? { ...r, notes: e.target.value }
                                          : r,
                                      ),
                                    )
                                  }
                                  placeholder="Optional"
                                  className="w-full rounded-md border border-[color:var(--color-border)] bg-[color:var(--color-bg)] px-2 py-1 text-sm focus:border-gold-500 focus:outline-none focus:ring-1 focus:ring-gold-500"
                                />
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                ))
              )}
            </div>
          )}
        </section>
      </div>
    </ListShell>
  );
}
